import type { LatencyPoint } from './model';

export type PlotGeometry = {
  yMaxMs: number;
  rowMs: number;
  /** The real pings, connected. A null sample breaks the line. */
  rawPath: string;
  last: { x: number; y: number; ping: number } | null;
};

/** Row-ms ladder: the first rung whose `rowMs * rows` covers the window's
 *  peak wins, so `yMaxMs` is always an exact multiple of `rowMs` — row
 *  lines never stop meaning a round number of milliseconds. Falls back to
 *  the top rung if even that doesn't cover an extreme peak. */
const ROW_MS_LADDER = [20, 50, 100, 200, 500, 1000];

/** Row-space y for a millisecond reading: 0 is the top of the plot, `rows`
 *  the baseline. Exported so the reference lines (p50/p99) are placed by the
 *  same mapping as the series itself — one scale, computed once. */
export function yFor(ms: number, rowMs: number, rows: number): number {
  return rows - ms / rowMs;
}

/**
 * Maps the last-hour series onto a plot area `rows` grid rows tall (x is a
 * unit width). The page grid's cell pitch is the gridlines: y = one row per `rowMs`,
 * chosen from `ROW_MS_LADDER` so a row line always means a round number of
 * milliseconds.
 *
 * x is anchored to the RIGHT against a fixed `window` of samples, not
 * stretched across whatever we happen to have: the newest sample sits on the
 * right edge and each older one steps back by one window slot. A probe that
 * has only reported a handful of times therefore draws a short line hugging
 * "now" — honest about how little history exists — instead of a full-width
 * curve that implies an hour of data.
 */
export function plotGeometry(
  series: LatencyPoint[],
  { rows, window }: { rows: number; window: number },
): PlotGeometry {
  const pings = series.map((p) => p.ping).filter((v): v is number => v !== null);
  const peak = pings.length ? Math.max(...pings) : 0;
  const rowMs = ROW_MS_LADDER.find((m) => m * rows >= peak) ?? ROW_MS_LADDER[ROW_MS_LADDER.length - 1];
  const yMaxMs = rowMs * rows;
  const n = series.length;
  if (n === 0) return { yMaxMs, rowMs, rawPath: '', last: null };

  // x is normalized to [0, 1] across the window: the SVG stretches it to
  // whatever whole-cell width the plot box has at the current breakpoint,
  // so the geometry never needs to know how many cells that is.
  const slot = 1 / Math.max(1, window - 1);
  const x = (i: number) => 1 - (n - 1 - i) * slot;
  const y = (ms: number) => yFor(ms, rowMs, rows);

  // A null ping means the check did not return a reading (down/degraded).
  // Skipping it would silently bridge the outage with a straight line, so
  // the gap starts a fresh subpath instead.
  let rawPath = '';
  let penDown = false;
  series.forEach((p, i) => {
    if (p.ping === null) {
      penDown = false;
      return;
    }
    rawPath += `${penDown ? ' L' : `${rawPath ? ' ' : ''}M`}${x(i).toFixed(4)} ${y(p.ping).toFixed(3)}`;
    penDown = true;
  });

  // The dot must sit at the newest REAL sample's own position, not the
  // right edge — a null-ping tail (down/degraded checks) would otherwise
  // draw a stale reading as if it were current.
  let lastIndex = -1;
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i].ping !== null) {
      lastIndex = i;
      break;
    }
  }
  const last =
    lastIndex >= 0
      ? { x: x(lastIndex), y: y(series[lastIndex].ping as number), ping: series[lastIndex].ping as number }
      : null;
  return { yMaxMs, rowMs, rawPath, last };
}
